import { useEffect } from "react";

export const SITE_URL = "https://visibleonline.in";

// Site-wide SEO defaults. These are the fallbacks used when a route doesn't
// supply its own values. They can be overridden at runtime from the
// admin-managed settings doc (settings/seo) via setSeoDefaults().
const defaults = {
  siteName: "Visible Online",
  title: "Visible Online — Your Business, Visible Online",
  description:
    "Visible Online helps local businesses get found, trusted, and chosen through Google Business Profile setup, professional websites, product catalogs, and local SEO.",
  keywords: "",
  image: "",
};

// Merges admin-managed values into the defaults. Blank/missing fields are
// ignored so an empty admin field never wipes out a good built-in default.
export function setSeoDefaults(partial = {}) {
  for (const key of ["siteName", "title", "description", "keywords", "image"]) {
    const val = partial[key];
    if (typeof val === "string" && val.trim()) defaults[key] = val.trim();
  }
}

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr, key) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Writes the document title, description, canonical, and social tags for the
// given route. Safe to call imperatively (e.g. after a blog post loads async
// from Firestore) as well as from the useSeo hook below.
export function applySeo({
  title,
  description,
  keywords,
  image,
  path = "/",
  noindex = false,
} = {}) {
  const fullTitle = title || defaults.title;
  const desc = description || defaults.description;
  const kw = keywords || defaults.keywords;
  const img = image || defaults.image;
  const url = SITE_URL + path;

  document.title = fullTitle;
  upsertMeta("name", "description", desc);
  if (kw) upsertMeta("name", "keywords", kw);
  else removeMeta("name", "keywords");
  upsertMeta("property", "og:site_name", defaults.siteName);
  upsertMeta("property", "og:title", fullTitle);
  upsertMeta("property", "og:description", desc);
  upsertMeta("property", "og:url", url);
  upsertMeta("name", "twitter:title", fullTitle);
  upsertMeta("name", "twitter:description", desc);
  if (img) {
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
  }
  upsertLink("canonical", url);

  if (noindex) {
    upsertMeta("name", "robots", "noindex, nofollow");
  } else {
    const robots = document.head.querySelector('meta[name="robots"]');
    if (robots) robots.remove();
  }
}

// Keeps <title>, description, canonical, and social tags in sync with the
// current route. This is an SPA, so without this every route would inherit the
// homepage's static tags — crawlers that render JS pick these up per page.
// Pass `noindex` for private routes (e.g. the admin portal).
export function useSeo({
  title,
  description,
  keywords,
  image,
  path = "/",
  noindex = false,
} = {}) {
  useEffect(() => {
    applySeo({ title, description, keywords, image, path, noindex });
  }, [title, description, keywords, image, path, noindex]);
}
